<template>
  <div id="retailer-turn-in-orders" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading agents' data..."
    />

    <v-row v-else class="g-container" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="account/retailer-turn-in-orders/approved-agents"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <i-local-filter
              :filterPlaceholder="'Search Agents by email'"
              @filter="localFilter"
            />
          </v-col>
        </v-row>

        <template v-if="!loading && filteredAgents.length === 0">
          <v-row>
            <v-col cols="12">
              <v-col class="pa-0">
                <i-empty-results
                  :buttonText="`${isILR ? 'Customer' : 'Retailer'} Turn In Orders`"
                  buttonRouteName="retailer-turn-in-orders"
                  :title="emptyResultsText"
                />
              </v-col>
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <template v-if="$vuetifyDisplay.smAndDown">
            <v-col
              class="mt-0 pa-0"
              v-for="item in filteredAgents"
              :key="item.email"
            >
              <v-card class="rounded-2 pa-4 mb-6">
                <v-row no-gutters>
                  <v-col class="py-1">
                    <span
                      ><strong>{{ agentsHeaders[0].text }}</strong></span
                    >
                    <br />
                    <span
                      ><strong>{{ item.email }}</strong></span
                    >
                  </v-col>
                </v-row>

                <v-divider class="my-2"></v-divider>

                <v-row no-gutters>
                  <v-col cols="12" class="py-1">
                    <span>{{ agentsHeaders[1].text }}</span>
                  </v-col>
                  <v-col cols="12" class="py-1">
                    <v-chip dense outlined small class="mr-1" color="primary">
                      Agent
                    </v-chip>
                  </v-col>
                  <v-col cols="12" class="py-1">
                    <span>{{ agentsHeaders[2].text }}</span>
                  </v-col>
                  <v-col cols="12" class="py-1">
                    <v-chip
                      dense
                      outlined
                      small
                      class="mr-1 mb-1"
                      color="primary"
                      v-for="store in item.stores"
                      :key="store.id"
                    >
                      {{ store.displayName }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </template>

          <v-data-table
            v-else
            class="pt-2 elevation-1 i-data-table"
            hide-default-footer
            item-key="email"
            :headers="agentsHeaders"
            :items="filteredAgents"
            :items-per-page="-1"
            :loading="loading"
          >
            <template #[`header.agent`]="{ header }">
              <span>
                {{ header.text }}
                <v-icon
                  class="i-tooltip-color pl-1 pb-1"
                  small
                  v-tooltip="{
                    content: 'Manage in Agent Invites & Management',
                    visible: $vuetifyDisplay.mdAndDown ? false : undefined
                  }"
                >
                  mdi-help-circle-outline
                </v-icon>
              </span>
            </template>

            <template #[`item.agent`]="{}">
              <v-chip dense outlined small class="mr-1" color="primary">
                Agent
              </v-chip>
            </template>

            <template #[`item.stores`]="{ item }">
              <v-chip
                dense
                outlined
                small
                class="mr-1"
                color="primary"
                v-for="store in item.stores"
                :key="store.id"
              >
                {{ store.displayName }}
              </v-chip>
            </template>
          </v-data-table>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { fetchAgents } from '@/services/userAccess'
import iEmptyResults from '@/components/EmptyResults'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'

export default {
  name: 'retailer-turn-in-orders',

  components: {
    iInstructions,
    iEmptyResults,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    loading: true,
    emptyResultsText: 'No approved agents found',
    filterText: '',
    agents: []
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['isILR']),

    agentsHeaders() {
      return [
        { text: 'EMAIL', value: 'email' },
        { text: 'ROLE', value: 'agent', sortable: false },
        {
          text: this.isILR ? 'CUSTOMER(S)' : 'RETAILER(S)',
          value: 'stores',
          sortable: false
        }
      ]
    },

    filteredAgents() {
      return this.agents.filter((agent) =>
        agent.email.includes(this.filterText.toLowerCase())
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    async fetchData() {
      try {
        this.agents = await fetchAgents(this.selectedSupplierId)
        this.loading = false
      } catch (err) {
        this.addNotification({ color: 'error', defaultError: true })
      }
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    }
  },

  mounted() {
    this.fetchData()
  }
}
</script>

<style lang="scss" scoped>
#retailer-turn-in-orders {
  .chip {
    font-size: 14px;
    height: 40px;
    min-width: 70px;
    border-radius: 50px;
    justify-content: center;
  }
  .td-chip {
    text-align: center;
  }
  .v-btn:not(.v-btn--text):not(.v-btn--outlined):hover:before {
    opacity: 0;
  }
  .v-list-item {
    &.v-list-item--active {
      background-color: var(--v-secondary-base);
      color: white;
    }
  }
}

@media only screen and (min-width: 960px) {
  #retailer-turn-in-orders {
    .v-data-table-header__icon {
      opacity: 1;
      margin-bottom: 3px;
    }
    .v-data-table-header__icon:hover {
      color: black;
    }
  }
}
</style>
